import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import { SkeletonRows } from 'components/Skeleton'
import { PRIVATE_PATHS } from 'utils/paths'
import {
  fetchTaskDomains,
  createTaskDomain,
  updateTaskDomain,
  deleteTaskDomain
} from 'services/ProgrammeService'
import {
  ink,
  surface,
  line,
  radius,
  shadow,
  font,
  transition
} from 'features/dashboard/tokens'

const SWATCHES = [
  '#b00020',
  '#c2410c',
  '#d97706',
  '#15803d',
  '#0e7490',
  '#33becc',
  '#6d28d9',
  '#9333ea',
  '#be185d',
  '#475569'
]

const DEFAULT_COLOUR = '#475569'

const emptyForm = {
  name: '',
  short: '',
  colour: DEFAULT_COLOUR,
  icon: '',
  description: '',
  aiPrompt: '',
  freqLabel: ''
}

const readResponseList = res => res?.data?.data ?? res?.data ?? []
const readResponseItem = res => res?.data?.data ?? res?.data ?? null

const TaskDomainsManager = () => {
  const navigate = useNavigate()
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const loadDomains = async () => {
    setLoading(true)
    try {
      const res = await fetchTaskDomains()
      setDomains(readResponseList(res))
    } catch (e) {
      toast.error('We could not load task domains. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDomains()
  }, [])

  const { systemDomains, customDomains } = useMemo(() => {
    const sorter = (a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      (a.name || '').localeCompare(b.name || '')
    return {
      systemDomains: domains.filter(d => d.isSystem).sort(sorter),
      customDomains: domains.filter(d => !d.isSystem).sort(sorter)
    }
  }, [domains])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setShowForm(true)
  }

  const openEdit = domain => {
    if (domain.isSystem) return
    setEditing(domain)
    setForm({
      name: domain.name || '',
      short: domain.short || '',
      colour: domain.colour || DEFAULT_COLOUR,
      icon: domain.icon || '',
      description: domain.description || '',
      aiPrompt: domain.aiPrompt || '',
      freqLabel: domain.freqLabel || ''
    })
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditing(null)
    setForm(emptyForm)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const name = form.name.trim()
    if (!name) {
      toast.error('Please give the domain a name.')
      return
    }
    const payload = {
      name,
      short: form.short.trim() || null,
      colour: form.colour || null,
      icon: form.icon.trim() || null,
      description: form.description.trim() || null,
      aiPrompt: form.aiPrompt.trim() || null,
      freqLabel: form.freqLabel.trim() || null
    }
    setSaving(true)
    try {
      if (editing) {
        await updateTaskDomain(editing.id, payload)
        toast.success('Domain updated.')
      } else {
        await createTaskDomain(payload)
        toast.success('Domain created.')
      }
      closeForm()
      await loadDomains()
    } catch (err) {
      toast.error(
        editing
          ? 'We could not update the domain. Please try again.'
          : 'We could not create the domain. Please try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async domain => {
    if (domain.isSystem) return
    const confirmed = window.confirm(
      `Delete the “${domain.name}” domain? This cannot be undone.`
    )
    if (!confirmed) return
    setDeletingId(domain.id)
    try {
      await deleteTaskDomain(domain.id)
      setDomains(prev => prev.filter(d => d.id !== domain.id))
      toast.success('Domain deleted.')
    } catch (err) {
      toast.error('We could not delete the domain. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const total = domains.length

  return (
    <Wrapper>
      <Header>
        <BackButton type="button" onClick={() => navigate(PRIVATE_PATHS.taskManagement)}>
          <ArrowBackIcon style={{ fontSize: 18 }} />
          <span>Back to tasks</span>
        </BackButton>
        <HeaderMain>
          <div>
            <Title>Task domains</Title>
            <Subtitle>
              Organise tasks by area of responsibility. Shared domains are
              available to everyone; create your own to suit how your
              organisation works.
            </Subtitle>
          </div>
          <NewButton type="button" onClick={openCreate}>
            <AddIcon style={{ fontSize: 18 }} />
            <span>New domain</span>
          </NewButton>
        </HeaderMain>
      </Header>

      {loading ? (
        <Card>
          <SkeletonRows rows={6} widths={['28px', '40%', '20%', '14%']} />
        </Card>
      ) : (
        <>
          <CountLine>Showing {total} domain{total === 1 ? '' : 's'}</CountLine>

          {customDomains.length > 0 && (
            <Section>
              <SectionTitle>Your domains</SectionTitle>
              <List>
                {customDomains.map(domain => (
                  <Row key={domain.id}>
                    <Swatch $colour={domain.colour || DEFAULT_COLOUR} />
                    <RowMain>
                      <RowName>{domain.name}</RowName>
                      {domain.short && <RowShort>{domain.short}</RowShort>}
                      {domain.description && (
                        <RowDescription>{domain.description}</RowDescription>
                      )}
                    </RowMain>
                    <RowActions>
                      <ActionButton
                        type="button"
                        onClick={() => openEdit(domain)}
                        aria-label={`Edit ${domain.name}`}
                        title="Edit"
                      >
                        <EditOutlinedIcon style={{ fontSize: 18 }} />
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $danger
                        disabled={deletingId === domain.id}
                        onClick={() => handleDelete(domain)}
                        aria-label={`Delete ${domain.name}`}
                        title="Delete"
                      >
                        <DeleteOutlineIcon style={{ fontSize: 18 }} />
                      </ActionButton>
                    </RowActions>
                  </Row>
                ))}
              </List>
            </Section>
          )}

          {systemDomains.length > 0 && (
            <Section>
              <SectionTitle>Shared domains</SectionTitle>
              <SectionHint>
                These are provided for every organisation and cannot be edited
                or removed.
              </SectionHint>
              <List>
                {systemDomains.map(domain => (
                  <Row key={domain.id} $muted>
                    <Swatch $colour={domain.colour || DEFAULT_COLOUR} />
                    <RowMain>
                      <RowNameLine>
                        <RowName>{domain.name}</RowName>
                        <SystemBadge>System</SystemBadge>
                      </RowNameLine>
                      {domain.short && <RowShort>{domain.short}</RowShort>}
                      {domain.description && (
                        <RowDescription>{domain.description}</RowDescription>
                      )}
                    </RowMain>
                    <SharedTag>
                      <LockOutlinedIcon style={{ fontSize: 14 }} />
                      <span>Shared</span>
                    </SharedTag>
                  </Row>
                ))}
              </List>
            </Section>
          )}

          {customDomains.length === 0 && systemDomains.length === 0 && (
            <EmptyState>
              <EmptyIcon>
                <LabelOutlinedIcon style={{ fontSize: 26 }} />
              </EmptyIcon>
              <EmptyTitle>No domains yet</EmptyTitle>
              <EmptyText>
                Create a domain to group tasks by the area of responsibility
                they belong to.
              </EmptyText>
              <NewButton type="button" onClick={openCreate}>
                <AddIcon style={{ fontSize: 18 }} />
                <span>New domain</span>
              </NewButton>
            </EmptyState>
          )}
        </>
      )}

      {showForm && createPortal(
        <Overlay onClick={closeForm}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editing ? 'Edit domain' : 'New domain'}</ModalTitle>
              <CloseButton type="button" onClick={closeForm} aria-label="Close">
                <CloseIcon style={{ fontSize: 18 }} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <Field>
                <Label htmlFor="domain-name">Name</Label>
                <Input
                  id="domain-name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Fire safety"
                  maxLength={80}
                  autoFocus
                />
              </Field>

              <Field>
                <Label htmlFor="domain-short">Short label (optional)</Label>
                <Input
                  id="domain-short"
                  type="text"
                  value={form.short}
                  onChange={e => setForm({ ...form, short: e.target.value })}
                  placeholder="e.g. Fire"
                  maxLength={24}
                />
              </Field>

              <Field>
                <Label>Colour</Label>
                <Palette>
                  {SWATCHES.map(c => (
                    <SwatchButton
                      key={c}
                      type="button"
                      $colour={c}
                      $selected={form.colour?.toLowerCase() === c.toLowerCase()}
                      onClick={() => setForm({ ...form, colour: c })}
                      aria-label={`Use colour ${c}`}
                    />
                  ))}
                  <ColourInputWrap>
                    <ColourInput
                      type="color"
                      value={form.colour || DEFAULT_COLOUR}
                      onChange={e => setForm({ ...form, colour: e.target.value })}
                      aria-label="Choose a custom colour"
                    />
                  </ColourInputWrap>
                </Palette>
              </Field>

              <Field>
                <Label htmlFor="domain-icon">Icon (optional)</Label>
                <Input
                  id="domain-icon"
                  type="text"
                  value={form.icon}
                  onChange={e => setForm({ ...form, icon: e.target.value })}
                  placeholder="e.g. local_fire_department"
                  maxLength={48}
                />
              </Field>

              <Field>
                <Label htmlFor="domain-description">Description (optional)</Label>
                <Textarea
                  id="domain-description"
                  rows={3}
                  value={form.description}
                  onChange={e =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="What kind of tasks belong to this domain?"
                  maxLength={240}
                />
              </Field>

              <Field>
                <Label htmlFor="domain-ai-prompt">
                  AI classification prompt (optional)
                </Label>
                <Textarea
                  id="domain-ai-prompt"
                  rows={4}
                  value={form.aiPrompt}
                  onChange={e => setForm({ ...form, aiPrompt: e.target.value })}
                  placeholder='Describe the documents or tasks that belong to this domain so Guru can file them automatically. E.g. "Fire risk assessments, EWS1 forms, FRAEW reports".'
                  maxLength={4000}
                />
                <FieldHint>
                  Lets Guru auto-file documents into this domain, just like the
                  built-in domains. Leave blank to assign manually.
                </FieldHint>
              </Field>

              <Field>
                <Label htmlFor="domain-freq-label">
                  Frequency label (optional)
                </Label>
                <Input
                  id="domain-freq-label"
                  type="text"
                  value={form.freqLabel}
                  onChange={e => setForm({ ...form, freqLabel: e.target.value })}
                  placeholder='e.g. "Annual"'
                  maxLength={64}
                />
              </Field>

              <ModalActions>
                <GhostButton type="button" onClick={closeForm}>
                  Cancel
                </GhostButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {saving
                    ? 'Saving…'
                    : editing
                      ? 'Save changes'
                      : 'Create domain'}
                </PrimaryButton>
              </ModalActions>
            </Form>
          </Modal>
        </Overlay>,
        document.body
      )}
    </Wrapper>
  )
}

export default TaskDomainsManager

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 880px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${font.small};
  font-weight: 600;
  color: ${ink.soft};
  &:hover { color: ${ink.base}; }
`

const HeaderMain = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Title = styled.h1`
  margin: 0;
  font-size: ${font.title};
  font-weight: 600;
  color: ${ink.base};
`

const Subtitle = styled.p`
  margin: 6px 0 0 0;
  font-size: ${font.body};
  color: ${ink.soft};
  max-width: 540px;
`

const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: none;
  border-radius: ${radius.sm};
  background: ${ink.base};
  color: #fff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${transition};
  &:hover { background: #1a2240; }
`

const Card = styled.div`
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
  padding: 16px;
`

const CountLine = styled.div`
  font-size: ${font.small};
  color: ${ink.mute};
  font-weight: 600;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const SectionHint = styled.p`
  margin: 0 0 2px 0;
  font-size: ${font.small};
  color: ${ink.mute};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: ${({ $muted }) => ($muted ? surface.wash : surface.card)};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
`

const Swatch = styled.span`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border-radius: ${radius.round};
  background: ${({ $colour }) => $colour};
  box-shadow: inset 0 0 0 1px rgba(12, 18, 41, 0.12);
`

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RowNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const RowName = styled.span`
  font-size: ${font.emphasis};
  font-weight: 600;
  color: ${ink.base};
`

const RowShort = styled.span`
  font-size: ${font.small};
  color: ${ink.soft};
`

const RowDescription = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${({ $danger }) => ($danger ? '#b00020' : ink.soft)};
  cursor: pointer;
  transition: ${transition};
  &:hover:not(:disabled) {
    background: ${({ $danger }) => ($danger ? '#fdecea' : surface.canvas)};
    border-color: ${({ $danger }) => ($danger ? '#f3c0bb' : line.strong)};
  }
  &:disabled { opacity: 0.5; cursor: default; }
`

const SystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: ${radius.pill};
  background: ${surface.canvas};
  border: 1px solid ${line.base};
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const SharedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: ${font.small};
  font-weight: 600;
  color: ${ink.mute};
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 48px 24px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
`

const EmptyIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: ${radius.round};
  background: ${surface.canvas};
  color: ${ink.mute};
`

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 600;
  color: ${ink.base};
`

const EmptyText = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: ${font.body};
  color: ${ink.soft};
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100020;
  background: rgba(12, 18, 41, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${surface.card};
  border-radius: ${radius.md};
  box-shadow: ${shadow.overlay};
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${line.base};
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 600;
  color: ${ink.base};
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${radius.sm};
  background: none;
  color: ${ink.mute};
  cursor: pointer;
  &:hover { background: ${surface.canvas}; color: ${ink.base}; }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: ${font.small};
  font-weight: 600;
  color: ${ink.soft};
`

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font-size: ${font.body};
  color: ${ink.base};
  background: ${surface.card};
  &:focus {
    outline: none;
    border-color: ${ink.base};
  }
`

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font-size: ${font.body};
  color: ${ink.base};
  background: ${surface.card};
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: ${ink.base};
  }
`

const FieldHint = styled.p`
  margin: 0;
  font-size: ${font.small};
  color: ${ink.mute};
`

const Palette = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const SwatchButton = styled.button`
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: ${radius.round};
  border: 2px solid ${({ $selected }) => ($selected ? ink.base : 'transparent')};
  background: ${({ $colour }) => $colour};
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(12, 18, 41, 0.12);
  transition: ${transition};
`

const ColourInputWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: ${radius.round};
  border: 1px dashed ${line.strong};
  overflow: hidden;
`

const ColourInput = styled.input`
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
`

const GhostButton = styled.button`
  padding: 9px 14px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${surface.canvas}; }
`

const PrimaryButton = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${radius.sm};
  background: ${ink.base};
  color: #fff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #1a2240; }
  &:disabled { opacity: 0.6; cursor: default; }
`
